import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  useTheme,
} from "@mui/material";
import { App, PimoReactComponent, View } from "@pimo/pimo-app-builder";
import { DragAndDropLayout, PMTitleCard } from "@pimo/pimo-components";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import { calculateMaturityGauge, getContrastColor } from "product-test-utils";
import React, { useState } from "react";
import { generatePath } from "react-router-dom";

import { TestAppState } from "../../../app";
import { APP_ROUTES } from "../../../constants";
import { theme } from "../../../theme";
import { getMaturityDefinitionLabelForValue } from "./dimension-overview-maturity";

type DimensionOverviewActivity = {
  id: string;
  name: string;
  maturity: string;
  completion: string;
};

type DimensionOverviewDimension = {
  name: string;
  maturity: string;
  maturityColor: string;
  completedActivities: number;
  activityCount: number;
  assessmentStatus: string;
  assessmentColor: string;
  activities: DimensionOverviewActivity[];
  onOpen: () => void;
};

type DimensionOverviewProps = {
  dimensionLabel: string;
  maturityLabel: string;
  completedActivitiesLabel: string;
  assessmentStatusLabel: string;
  openLabel: string;
  idLabel: string;
  activityLabel: string;
  completionLabel: string;
  dimensions: DimensionOverviewDimension[];
};

export const DimensionOverview: PimoReactComponent<DimensionOverviewProps> = ({
  dimensionLabel,
  maturityLabel,
  completedActivitiesLabel,
  assessmentStatusLabel,
  openLabel,
  idLabel,
  activityLabel,
  completionLabel,
  dimensions,
}) => {
  const theme = useTheme();
  const [openRowIndex, setOpenRowIndex] = useState<number | null>(null);
  const [activitySort, setActivitySort] = useState<{
    key: keyof DimensionOverviewActivity;
    direction: "asc" | "desc";
  } | null>(null);

  const handleToggle = (index: number) => {
    setOpenRowIndex((currentIndex) => (currentIndex === index ? null : index));
  };

  const handleActivitySort = (key: keyof DimensionOverviewActivity) => {
    setActivitySort((current) =>
      current?.key === key
        ? { key, direction: current.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    );
  };

  const getSortedActivities = (activities: DimensionOverviewActivity[]) => {
    if (!activitySort) {
      return activities;
    }
    const { key, direction } = activitySort;
    const sorted = [...activities].sort((a, b) =>
      (a[key] ?? "").localeCompare(b[key] ?? "", undefined, { numeric: true })
    );
    return direction === "asc" ? sorted : sorted.reverse();
  };

  const SummaryCell = ({
    label,
    children,
    sx,
    onClick,
  }: {
    label: string;
    children: React.ReactNode;
    sx?: object;
    onClick?: (event: React.MouseEvent<HTMLTableCellElement>) => void;
  }) => (
    <TableCell
      onClick={onClick}
      sx={{ border: 0, py: 1.5, verticalAlign: "middle", ...sx }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Typography variant="subtitle2">{label}</Typography>
        {children}
      </Box>
    </TableCell>
  );

  return (
    <TableContainer
      component={Paper}
      sx={{
        backgroundColor: theme.palette.secondary.main,
        borderRadius: 3,
        boxShadow: "none",
        p: 1,
      }}
    >
      <Table
        sx={{
          borderCollapse: "separate",
          borderSpacing: 0,
        }}
      >
        <TableBody>
          {dimensions.map((dimension, index) => {
            const isOpen = openRowIndex === index;
            return (
              <React.Fragment key={dimension.name}>
                {index > 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      sx={{ border: 0, p: 0, height: 16 }}
                    />
                  </TableRow>
                )}
                <TableRow
                  onClick={() => handleToggle(index)}
                  sx={{
                    backgroundColor: theme.palette.background.paper,
                    cursor: "pointer",
                    "& td:first-of-type": {
                      borderTopLeftRadius: 12,
                      borderBottomLeftRadius: isOpen ? 0 : 12,
                    },
                    "& td:last-of-type": {
                      borderTopRightRadius: 12,
                      borderBottomRightRadius: isOpen ? 0 : 12,
                    },
                  }}
                >
                  <SummaryCell label={dimensionLabel} sx={{ minWidth: 220 }}>
                    <Typography variant="body2">{dimension.name}</Typography>
                  </SummaryCell>
                  <SummaryCell label={maturityLabel} sx={{ minWidth: 150 }}>
                    <Chip
                      label={dimension.maturity}
                      size="small"
                      sx={{
                        width: "fit-content",
                        backgroundColor: dimension.maturityColor,
                        color: getContrastColor(
                          dimension.maturityColor,
                          theme.palette.text.primary
                        ),
                        fontWeight: 600,
                      }}
                    />
                  </SummaryCell>
                  <SummaryCell
                    label={completedActivitiesLabel}
                    sx={{ minWidth: 180 }}
                  >
                    <Typography variant="body2">
                      {dimension.completedActivities}/{dimension.activityCount}
                    </Typography>
                  </SummaryCell>
                  <SummaryCell
                    label={assessmentStatusLabel}
                    sx={{ minWidth: 170 }}
                  >
                    <Chip
                      label={dimension.assessmentStatus}
                      size="small"
                      sx={{
                        width: "fit-content",
                        backgroundColor: dimension.assessmentColor,
                        color: getContrastColor(
                          dimension.assessmentColor,
                          theme.palette.text.primary
                        ),
                        fontWeight: 500,
                      }}
                    />
                  </SummaryCell>
                  <SummaryCell label="" sx={{ minWidth: 120 }}>
                    <Button
                      variant="contained"
                      size="medium"
                      onClick={(event) => {
                        event.stopPropagation();
                        dimension.onOpen();
                      }}
                      sx={{
                        padding: "0 12px",
                        borderRadius: "4px",
                        backgroundColor: "#F86200",
                        color: "#fff",
                        textTransform: "none",
                        boxShadow: "none",
                        fontSize: "14px",
                        height: "36px",
                        minWidth: 100,
                        "&:hover": {
                          boxShadow: "none",
                          backgroundColor: "#ff8c42",
                        },
                      }}
                    >
                      {openLabel}
                    </Button>
                  </SummaryCell>
                  <TableCell sx={{ border: 0, width: 48, px: 1 }}>
                    <IconButton
                      aria-label={`${isOpen ? "collapse" : "expand"} ${dimension.name}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleToggle(index);
                      }}
                    >
                      {isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </IconButton>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    colSpan={6}
                    sx={{
                      p: 0,
                      border: 0,
                      backgroundColor: theme.palette.background.paper,
                      borderBottomLeftRadius: 12,
                      borderBottomRightRadius: 12,
                      overflow: "hidden",
                    }}
                  >
                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                      <Table
                        size="small"
                        sx={{
                          borderTop: `1px solid ${theme.palette.secondary.main}`,
                          "& th, & td": {
                            borderColor: theme.palette.secondary.main,
                            height: 72,
                            py: 1.5,
                          },
                          "& th": {
                            height: 48,
                            py: 1,
                          },
                        }}
                      >
                        <TableHead>
                          <TableRow>
                            {(
                              [
                                { key: "id", label: idLabel },
                                { key: "name", label: activityLabel },
                                { key: "maturity", label: maturityLabel },
                                { key: "completion", label: completionLabel },
                              ] as const
                            ).map(({ key, label }) => (
                              <TableCell key={key}>
                                <TableSortLabel
                                  active={activitySort?.key === key}
                                  direction={
                                    activitySort?.key === key
                                      ? activitySort.direction
                                      : "asc"
                                  }
                                  onClick={() => handleActivitySort(key)}
                                >
                                  {label}
                                </TableSortLabel>
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {getSortedActivities(dimension.activities).map(
                            (activity) => (
                              <TableRow key={activity.id}>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 400 }}
                                  >
                                    {activity.id}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 400 }}
                                  >
                                    {activity.name}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  {activity.maturity &&
                                  activity.maturity !== "-" ? (
                                    <Chip
                                      label={activity.maturity}
                                      size="small"
                                      sx={{
                                        backgroundColor:
                                          theme.palette.secondary.main,
                                        color: theme.palette.text.primary,
                                        borderRadius: 8,
                                        fontWeight: 600,
                                      }}
                                    />
                                  ) : (
                                    "-"
                                  )}
                                </TableCell>
                                <TableCell>
                                  {activity.completion &&
                                  activity.completion !== "-" ? (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          width: 10,
                                          height: 10,
                                          borderRadius: "50%",
                                          flexShrink: 0,
                                          backgroundColor:
                                            activity.completion.toLowerCase() ===
                                            "completed"
                                              ? theme.palette.success.main
                                              : activity.completion.toLowerCase() ===
                                                  "in progress"
                                                ? theme.palette.warning.main
                                                : theme.palette.grey[400],
                                        }}
                                      />
                                      <Typography variant="body2">
                                        {activity.completion}
                                      </Typography>
                                    </Box>
                                  ) : (
                                    "-"
                                  )}
                                </TableCell>
                              </TableRow>
                            )
                          )}
                        </TableBody>
                      </Table>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export function addDimensionOverviewCard(
  view: View<TestAppState, DragAndDropLayout>,
  app: App<TestAppState>
) {
  const language = app.getAppState().language || "en";

  const titleComponent = view.addComponent({
    component: PMTitleCard,
    layoutProps: {
      componentId: "dimensionOverviewTitleComponent",
      labels: ["dimension", "maturity", "title"],
      title:
        getLocalizedMessage(language, "cards.dimensionOverview.title") ||
        "Dimension Overview",
      description:
        getLocalizedMessage(language, "cards.dimensionOverview.description") ||
        "",
    },
  });

  titleComponent.mapState(({ language: lang }) => ({
    title:
      getLocalizedMessage(lang || "en", "cards.dimensionOverview.title") ||
      "Dimension Overview",
    showInfoIcon: false,
    showMenuIcon: false,
  }));

  titleComponent.mapLayoutProps(({ language: lang }) => ({
    componentId: "dimensionOverviewTitleComponent",
    labels: ["dimension", "maturity", "title"],
    title:
      getLocalizedMessage(lang || "en", "cards.dimensionOverview.title") ||
      "Dimension Overview",
    description:
      getLocalizedMessage(
        lang || "en",
        "cards.dimensionOverview.description"
      ) || "",
  }));

  const dimensionTableComponent = view.addComponent({
    component: DimensionOverview,
    layoutProps: {
      componentId: "dimensionTableComponent",
      labels: ["dimension", "maturity", "table"],
      title: "",
      description:
        getLocalizedMessage(language, "cards.dimensionOverview.description") ||
        "",
    },
  });

  dimensionTableComponent.mapLayoutProps(({ language }) => {
    const currentLanguage = language || "en";
    return {
      componentId: "dimensionTableComponent",
      labels: ["dimension", "maturity", "table"],
      title: "",
      description:
        getLocalizedMessage(
          currentLanguage,
          "cards.dimensionOverview.description"
        ) || "",
    };
  });

  dimensionTableComponent.mapState(
    ({ currentReport, settings, language: lang }) => {
      const dimensions = Array.from(
        new Set(
          (currentReport?.project?.reportingItems ?? []).map(
            (item) => item.basicItem?.dimension || "No Dimension"
          )
        )
      );

      const gaugeState = calculateMaturityGauge({
        items: currentReport?.project?.reportingItems ?? [],
        configuration: settings?.configuration,
      });

      return {
        showInfoIcon: false,
        showMenuIcon: false,
        dimensionLabel:
          settings?.configuration?.fieldsDefinition
            ?.filter((field) => field.purpose === "itemBasic")
            .find((field) => field.fieldName === "dimension")?.label ||
          getLocalizedMessage(lang, "dimension") ||
          "Dimension",
        maturityLabel: getLocalizedMessage(lang, "maturity") || "Maturity",
        completedActivitiesLabel:
          getLocalizedMessage(lang, "completedActivities") ||
          "Completed Activities",
        assessmentStatusLabel:
          getLocalizedMessage(lang, "assessmentStatus") || "Assessment Status",
        openLabel: getLocalizedMessage(lang, "open") || "Open",
        idLabel: getLocalizedMessage(lang, "id") || "ID",
        activityLabel: getLocalizedMessage(lang, "activity") || "Activity",
        completionLabel:
          getLocalizedMessage(lang, "completion") || "Completion",
        dimensions: dimensions.map((dimensionName) => {
          const dimensionGauge = gaugeState?.dimensions.find(
            (dimension) => dimension.name === dimensionName
          );
          const maturityValue = dimensionGauge?.value ?? 0;
          const maturityColor =
            dimensionGauge?.color ?? theme.palette.secondary.light;
          const maturityNumberLabel = maturityValue.toFixed(2);
          const maturityDefinitionLabel = getMaturityDefinitionLabelForValue(
            maturityValue,
            settings?.configuration?.maturityConfiguration
          );
          const displayedMaturityLabel = maturityDefinitionLabel
            ? `${maturityNumberLabel} ${maturityDefinitionLabel}`
            : maturityNumberLabel;

          const dimensionActivities = (
            currentReport?.project?.reportingItems ?? []
          )
            .filter(
              (item) =>
                (item.basicItem?.dimension || "No Dimension") === dimensionName
            )
            .map((item) => ({
              id: item.basicItem?.customId || item.id || "-",
              name: item.basicItem?.name || "-",
              maturity: item.basicItem?.maturity || "-",
              completion: item.degreeOfCompletion || "-",
            }));
          const completedActivities = dimensionActivities.filter(
            (activity) => activity.completion.toLowerCase() === "completed"
          ).length;
          const completionStatus =
            currentReport?.project?._calculated?.dimensionFormCompletion?.[
              dimensionName
            ] ?? "not-started";
          const completionColors: Record<
            string,
            { bg: string; label: string }
          > = {
            "not-started": { bg: "#9E9E9E", label: "Not Started" },
            "in-progress": { bg: "#FF9800", label: "In Progress" },
            completed: { bg: "#4CAF50", label: "Completed" },
          };
          const { bg, label } =
            completionColors[completionStatus] ??
            completionColors["not-started"];

          return {
            name: dimensionName,
            maturity: displayedMaturityLabel,
            maturityColor,
            completedActivities,
            activityCount: dimensionActivities.length,
            assessmentStatus: label,
            assessmentColor: bg,
            activities: dimensionActivities,
            onOpen: () => {
              if (!currentReport?.project?.id) {
                return;
              }
              app.navigate(
                generatePath(APP_ROUTES.editReportByDimension, {
                  id: String(currentReport.project.id),
                  viewname: "dashboard",
                  dimension: dimensionName,
                })
              );
            },
          };
        }),
      };
    }
  );

  dimensionTableComponent.mapVisibility(({ settings }) => {
    const isDimensionEnabled =
      settings?.configuration?.fieldsDefinition
        ?.filter((field) => field.purpose === "itemBasic")
        .find((field) => field.fieldName === "dimension")?.enabled ?? false;
    return (
      settings?.configuration?.useCase === "maturity" && isDimensionEnabled
    );
  });
}
